import { createTheme, alpha } from '@mui/material/styles';

// Shared typography
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontWeight: 900, letterSpacing: '-0.02em' },
  h2: { fontWeight: 800, letterSpacing: '-0.01em' },
  h3: { fontWeight: 700, letterSpacing: '-0.01em' },
  h4: { fontWeight: 700 },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600 },
  button: { textTransform: 'none', fontWeight: 600 },
};

const shape = { borderRadius: 4 };

// ─── Dark palette ───
const darkPalette = {
  mode: 'dark',
  background: { default: '#050505', paper: '#0a0a0a' },
  primary: { main: '#ffffff', light: '#f5f5f5', dark: '#d4d4d4', contrastText: '#050505' },
  secondary: { main: '#a3a3a3', light: '#d4d4d4', dark: '#737373', contrastText: '#050505' },
  info: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb', contrastText: '#ffffff' },
  success: { main: '#22c55e', light: '#4ade80', dark: '#16a34a', contrastText: '#050505' },
  error: { main: '#ef4444', light: '#f87171', dark: '#dc2626', contrastText: '#ffffff' },
  warning: { main: '#eab308', light: '#facc15', dark: '#ca8a04', contrastText: '#050505' },
  text: { primary: '#e5e5e5', secondary: '#a3a3a3', disabled: '#525252' },
  divider: '#262626',
};

// ─── Light palette ───
const lightPalette = {
  mode: 'light',
  background: { default: '#fafafa', paper: '#ffffff' },
  primary: { main: '#171717', light: '#404040', dark: '#0a0a0a', contrastText: '#ffffff' },
  secondary: { main: '#525252', light: '#737373', dark: '#404040', contrastText: '#ffffff' },
  info: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb', contrastText: '#ffffff' },
  success: { main: '#16a34a', light: '#22c55e', dark: '#15803d', contrastText: '#ffffff' },
  error: { main: '#dc2626', light: '#ef4444', dark: '#b91c1c', contrastText: '#ffffff' },
  warning: { main: '#ca8a04', light: '#eab308', dark: '#a16207', contrastText: '#ffffff' },
  text: { primary: '#171717', secondary: '#525252', disabled: '#a3a3a3' },
  divider: '#e5e5e5',
};

// ─── Dark component overrides ───
const darkOverrides = {
  MuiButton: {
    styleOverrides: {
      root: { borderRadius: 2, padding: '8px 20px', fontSize: '0.95rem', transition: 'all 0.2s ease' },
      contained: {
        backgroundColor: '#ffffff', color: '#050505', boxShadow: 'none',
        '&:hover': { backgroundColor: '#d4d4d4', boxShadow: 'none' },
      },
      outlined: {
        borderColor: '#262626', color: '#e5e5e5',
        '&:hover': { borderColor: '#525252', backgroundColor: 'rgba(255, 255, 255, 0.03)' },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none', backgroundColor: '#0a0a0a', border: '1px solid #262626', boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)' },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: { backgroundColor: '#0a0a0a', border: '1px solid #262626', borderRadius: 4, transition: 'all 0.2s ease', '&:hover': { borderColor: '#404040' } },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 2, fontWeight: 600, fontSize: '0.85rem' },
      filled: { backgroundColor: '#262626', color: '#e5e5e5' },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': { borderColor: '#262626' },
          '&:hover fieldset': { borderColor: '#404040' },
          '&.Mui-focused fieldset': { borderColor: '#737373' },
        },
        '& .MuiInputLabel-root': { color: '#737373' },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#262626' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#404040' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#737373' },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { borderBottom: '1px solid #262626' },
      head: { backgroundColor: '#111111', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: '"JetBrains Mono", "Fira Code", monospace' },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 2, border: '1px solid' },
      standardInfo: { backgroundColor: 'rgba(59, 130, 246, 0.08)', borderColor: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa' },
      standardSuccess: { backgroundColor: 'rgba(34, 197, 94, 0.08)', borderColor: 'rgba(34, 197, 94, 0.2)', color: '#4ade80' },
      standardWarning: { backgroundColor: 'rgba(234, 179, 8, 0.08)', borderColor: 'rgba(234, 179, 8, 0.2)', color: '#facc15' },
      standardError: { backgroundColor: 'rgba(239, 68, 68, 0.08)', borderColor: 'rgba(239, 68, 68, 0.2)', color: '#f87171' },
    },
  },
  MuiAppBar: { styleOverrides: { root: { backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none' } } },
  MuiDialog: { styleOverrides: { paper: { backgroundColor: '#0a0a0a', backgroundImage: 'none', border: '1px solid #262626', borderRadius: 4 } } },
  MuiLinearProgress: { styleOverrides: { root: { backgroundColor: '#262626', borderRadius: 2 }, bar: { backgroundColor: '#ffffff', borderRadius: 2 } } },
  MuiStepper: {
    styleOverrides: {
      root: {
        '& .MuiStepIcon-root': { color: '#262626', '&.Mui-active': { color: '#ffffff' }, '&.Mui-completed': { color: '#22c55e' } },
        '& .MuiStepLabel-label': { color: '#737373', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem', '&.Mui-active': { color: '#e5e5e5' }, '&.Mui-completed': { color: '#a3a3a3' } },
        '& .MuiStepConnector-line': { borderColor: '#262626' },
      },
    },
  },
  MuiMenu: { styleOverrides: { paper: { backgroundColor: '#0a0a0a', border: '1px solid #262626', borderRadius: 4 } } },
  MuiMenuItem: {
    styleOverrides: {
      root: { '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' }, '&.Mui-selected': { backgroundColor: 'rgba(255, 255, 255, 0.05)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } } },
    },
  },
  MuiCircularProgress: { styleOverrides: { root: { color: '#ffffff' } } },
};

// ─── Light component overrides ───
const lightOverrides = {
  MuiButton: {
    styleOverrides: {
      root: { borderRadius: 2, padding: '8px 20px', fontSize: '0.95rem', transition: 'all 0.2s ease' },
      contained: {
        backgroundColor: '#171717', color: '#ffffff', boxShadow: 'none',
        '&:hover': { backgroundColor: '#404040', boxShadow: 'none' },
      },
      outlined: {
        borderColor: '#e5e5e5', color: '#171717',
        '&:hover': { borderColor: '#d4d4d4', backgroundColor: 'rgba(0, 0, 0, 0.03)' },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none', backgroundColor: '#ffffff', border: '1px solid #e5e5e5', boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)' },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: { backgroundColor: '#ffffff', border: '1px solid #e5e5e5', borderRadius: 4, transition: 'all 0.2s ease', '&:hover': { borderColor: '#d4d4d4' } },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 2, fontWeight: 600, fontSize: '0.85rem' },
      filled: { backgroundColor: '#f5f5f5', color: '#171717' },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': { borderColor: '#e5e5e5' },
          '&:hover fieldset': { borderColor: '#d4d4d4' },
          '&.Mui-focused fieldset': { borderColor: '#737373' },
        },
        '& .MuiInputLabel-root': { color: '#737373' },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e5e5' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d4' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#737373' },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { borderBottom: '1px solid #e5e5e5' },
      head: { backgroundColor: '#f5f5f5', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: '"JetBrains Mono", "Fira Code", monospace' },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 2, border: '1px solid' },
      standardInfo: { backgroundColor: 'rgba(59, 130, 246, 0.06)', borderColor: 'rgba(59, 130, 246, 0.15)', color: '#2563eb' },
      standardSuccess: { backgroundColor: 'rgba(34, 197, 94, 0.06)', borderColor: 'rgba(34, 197, 94, 0.15)', color: '#16a34a' },
      standardWarning: { backgroundColor: 'rgba(234, 179, 8, 0.06)', borderColor: 'rgba(234, 179, 8, 0.15)', color: '#a16207' },
      standardError: { backgroundColor: 'rgba(239, 68, 68, 0.06)', borderColor: 'rgba(239, 68, 68, 0.15)', color: '#dc2626' },
    },
  },
  MuiAppBar: { styleOverrides: { root: { backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none' } } },
  MuiDialog: { styleOverrides: { paper: { backgroundColor: '#ffffff', backgroundImage: 'none', border: '1px solid #e5e5e5', borderRadius: 4 } } },
  MuiLinearProgress: { styleOverrides: { root: { backgroundColor: '#e5e5e5', borderRadius: 2 }, bar: { backgroundColor: '#171717', borderRadius: 2 } } },
  MuiStepper: {
    styleOverrides: {
      root: {
        '& .MuiStepIcon-root': { color: '#e5e5e5', '&.Mui-active': { color: '#171717' }, '&.Mui-completed': { color: '#16a34a' } },
        '& .MuiStepLabel-label': { color: '#737373', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem', '&.Mui-active': { color: '#171717' }, '&.Mui-completed': { color: '#525252' } },
        '& .MuiStepConnector-line': { borderColor: '#e5e5e5' },
      },
    },
  },
  MuiMenu: { styleOverrides: { paper: { backgroundColor: '#ffffff', border: '1px solid #e5e5e5', borderRadius: 4 } } },
  MuiMenuItem: {
    styleOverrides: {
      root: { '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.03)' }, '&.Mui-selected': { backgroundColor: 'rgba(0, 0, 0, 0.05)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } } },
    },
  },
  MuiCircularProgress: { styleOverrides: { root: { color: '#171717' } } },
};

// ─── Factory ───
export function createAppTheme(mode = 'dark') {
  const isDark = mode === 'dark';
  return createTheme({
    palette: isDark ? darkPalette : lightPalette,
    typography,
    shape,
    components: isDark ? darkOverrides : lightOverrides,
  });
}

// Mode-aware helpers
export const getGradients = (mode) => {
  const isDark = mode === 'dark';
  return {
    primary: isDark ? 'linear-gradient(90deg, #ffffff 0%, #d4d4d4 100%)' : 'linear-gradient(90deg, #171717 0%, #404040 100%)',
    secondary: isDark ? 'linear-gradient(90deg, #a3a3a3 0%, #737373 100%)' : 'linear-gradient(90deg, #525252 0%, #737373 100%)',
    success: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
    dark: isDark ? 'linear-gradient(180deg, #050505 0%, #0a0a0a 100%)' : 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)',
    card: (a = 0.03) => isDark
      ? `linear-gradient(135deg, rgba(255, 255, 255, ${a}) 0%, rgba(255, 255, 255, ${a * 0.5}) 100%)`
      : `linear-gradient(135deg, rgba(0, 0, 0, ${a}) 0%, rgba(0, 0, 0, ${a * 0.5}) 100%)`,
  };
};

export const getShadows = (mode) => {
  const isDark = mode === 'dark';
  return {
    sm: isDark ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.06)',
    md: isDark ? '0 4px 16px rgba(0, 0, 0, 0.5)' : '0 4px 16px rgba(0, 0, 0, 0.08)',
    lg: isDark ? '0 8px 32px rgba(0, 0, 0, 0.6)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
    glow: (color) => `0 4px 20px ${alpha(color, 0.3)}`,
  };
};

// Backward compat exports (dark mode defaults)
export const gradients = getGradients('dark');
export const shadows = getShadows('dark');
export default createAppTheme('dark');
